import { cva, type VariantProps } from 'class-variance-authority'
import { TextArea as AriaTextArea, type TextAreaProps as AriaTextAreaProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Textarea}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @today/tokens
 * (resolved per bound brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm, md (default), lg
 * - Fluid width by default (`w-full`)
 * - Min-height enforced to prevent single-line collapse
 * - Focus ring uses the bound brand's primary colour
 *
 * @example
 * ```tsx
 * const classes = textareaVariants({ size: 'md' })
 * // Returns: "w-full rounded-ds-md border ... min-h-[80px] ..."
 * ```
 */
export const textareaVariants = cva(
	// The invalid state is driven by `aria-invalid`, which FieldShell injects onto the
	// control when an error is present — so the ds-danger border lights up with no extra
	// prop to wire, and a manual `aria-invalid` on a bare <Textarea> styles identically.
	'w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 py-2 text-ds-base text-ds-text transition-colors placeholder:text-ds-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50 resize-y aria-[invalid=true]:border-ds-danger aria-[invalid=true]:focus-visible:ring-ds-danger',
	{
		variants: {
			size: {
				sm: 'text-ds-sm px-2 py-1.5 min-h-[60px]',
				md: 'text-ds-base px-3 py-2 min-h-[80px]',
				lg: 'text-ds-lg px-4 py-3 min-h-[100px]',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Textarea} component.
 *
 * Extends React Aria's TextAreaProps with CVA size variants.
 *
 * @property size - Textarea size: 'sm' | 'md' (default) | 'lg'
 * @property className - Optional additional Tailwind classes
 * @property placeholder - Placeholder text
 * @property isDisabled - Disabled state (React Aria pattern)
 * @property isReadOnly - Read-only state
 * @property isRequired - Required field indicator
 *
 * @example
 * ```tsx
 * <Textarea
 *   size="md"
 *   placeholder="Enter your comments"
 *   isRequired
 * />
 * ```
 */
export interface TextareaProps extends Omit<AriaTextAreaProps, 'className'>, VariantProps<typeof textareaVariants> {
	className?: string
}

/**
 * Accessible multiline text input primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation, focus management, and ARIA attributes
 *   provided by React Aria's `<TextArea>` primitive.
 * - **Styling:** All colors, spacing, and sizing use design tokens from `@today/tokens`
 *   (resolved per bound brand). No hardcoded values.
 * - **Sizes:** Small (min-h-60px), medium (min-h-80px, default), large (min-h-100px).
 * - **Fluid width:** Uses `w-full` by default to fill container.
 * - **Resizable:** Vertical resize enabled by default (`resize-y`).
 * - **Focus indicator:** 2px ring in the bound brand's primary colour.
 * - **Invalid state:** when `aria-invalid="true"` is present — set automatically by
 *   `FieldShell` whenever it has an error — the border and focus ring turn `ds-danger`.
 *
 * @example Basic usage
 * ```tsx
 * import { Textarea } from '@today/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <form>
 *       <Textarea
 *         placeholder="Enter your message"
 *         isRequired
 *       />
 *     </form>
 *   )
 * }
 * ```
 *
 * @example With controlled value
 * ```tsx
 * const [value, setValue] = useState('')
 *
 * <Textarea
 *   value={value}
 *   onChange={setValue}
 *   placeholder="Type here..."
 *   size="lg"
 * />
 * ```
 *
 * @see {@link textareaVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/textarea/textarea.stories.tsx for visual examples
 */
export function Textarea({ size, className, ...props }: TextareaProps) {
	return <AriaTextArea {...props} className={cn(textareaVariants({ size }), className)} />
}
